export interface Rect { x: number; y: number; w: number; h: number }
/** A rect rotated about its centre. BoardObjects satisfy this. */
export interface Box extends Rect { rotation: number }

export function center(b: Rect): [number, number] {
  return [b.x + b.w / 2, b.y + b.h / 2];
}

/** Axis-aligned bounds of a (possibly rotated) box. */
export function aabb(b: Box): Rect {
  if (!b.rotation) return { x: b.x, y: b.y, w: b.w, h: b.h };
  const c = Math.abs(Math.cos(b.rotation));
  const s = Math.abs(Math.sin(b.rotation));
  const w = b.w * c + b.h * s;
  const h = b.w * s + b.h * c;
  const [cx, cy] = center(b);
  return { x: cx - w / 2, y: cy - h / 2, w, h };
}

export function intersects(a: Rect, b: Rect): boolean {
  return a.x <= b.x + b.w && b.x <= a.x + a.w && a.y <= b.y + b.h && b.y <= a.y + a.h;
}

export function union(rects: Iterable<Rect>): Rect | null {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const r of rects) {
    x0 = Math.min(x0, r.x); y0 = Math.min(y0, r.y);
    x1 = Math.max(x1, r.x + r.w); y1 = Math.max(y1, r.y + r.h);
  }
  return x0 === Infinity ? null : { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

export function rectFromPoints(ax: number, ay: number, bx: number, by: number): Rect {
  return { x: Math.min(ax, bx), y: Math.min(ay, by), w: Math.abs(bx - ax), h: Math.abs(by - ay) };
}

export function rotatePoint(px: number, py: number, cx: number, cy: number, angle: number): [number, number] {
  const c = Math.cos(angle), s = Math.sin(angle);
  const dx = px - cx, dy = py - cy;
  return [cx + dx * c - dy * s, cy + dx * s + dy * c];
}

/** World point → box-local coordinates (origin at the unrotated top-left). */
export function toLocal(b: Box, px: number, py: number): [number, number] {
  const [cx, cy] = center(b);
  const [lx, ly] = rotatePoint(px, py, cx, cy, -b.rotation);
  return [lx - b.x, ly - b.y];
}

/** Box-local coordinates → world point. */
export function fromLocal(b: Box, lx: number, ly: number): [number, number] {
  const [cx, cy] = center(b);
  return rotatePoint(b.x + lx, b.y + ly, cx, cy, b.rotation);
}

export type Matrix = [a: number, b: number, c: number, d: number, e: number, f: number];

/**
 * Affine matrix mapping box-local coords (origin at the unrotated top-left) to
 * a target space scaled by `s` and offset by (tx, ty). Used for both canvas
 * transforms and CSS transforms so the two always agree.
 */
export function objectMatrix(o: Box, s: number, tx: number, ty: number): Matrix {
  if (!o.rotation) return [s, 0, 0, s, tx + o.x * s, ty + o.y * s];
  const cos = Math.cos(o.rotation), sin = Math.sin(o.rotation);
  const a = s * cos, b = s * sin, c = -s * sin, d = s * cos;
  const cx = o.x + o.w / 2, cy = o.y + o.h / 2;
  const hw = o.w / 2, hh = o.h / 2;
  return [a, b, c, d, tx + cx * s - a * hw - c * hh, ty + cy * s - b * hw - d * hh];
}

export function normalizeAngle(a: number): number {
  const t = Math.PI * 2;
  a %= t;
  if (a > Math.PI) a -= t;
  else if (a <= -Math.PI) a += t;
  return Math.abs(a) < 1e-9 ? 0 : a;
}

/** Squared distance from (px, py) to segment a–b. */
export function distToSegmentSq(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * dx - px, cy = ay + t * dy - py;
  return cx * cx + cy * cy;
}
